import { useState, useEffect } from 'react';
import type { Driver } from '../types/driver';
import { getAllDrivers, getSubunitNames, getRankings, getMeScore } from '../lib/api/endpoints';
import { driverFromDto, rankingEntryToScore, ownScoreToDriverScore } from '../lib/api/mappers';
import { useAuth } from '../context/AuthContext';
import { shareInFlight } from '../utils/shareInFlight';

// FE-04 trajo la identidad real de GET /drivers (ese endpoint nunca entrega
// `score` — ver mappers.ts). FE-16 la completa con el score real: `/rankings`
// para Admin/Supervisor/Ejecutivo (el Operador recibe 403 ahí, así que usa
// `/me/score` para el suyo). Si cualquiera de las dos llamadas falla, el
// conductor se queda sin `score` — la UI ya trata eso como "Pendiente"
// (RankingTable.tsx), nunca como un valor inventado.
async function withScores(drivers: Driver[], role: string | undefined, userId: string | undefined): Promise<Driver[]> {
  if (role === 'operator') {
    const own = drivers.find(d => d.userId === userId);
    if (!own) return drivers;
    try {
      const score = ownScoreToDriverScore(await getMeScore());
      return drivers.map(d => d.id === own.id ? { ...d, score } : d);
    } catch {
      return drivers;
    }
  }

  try {
    const ranking = await getRankings();
    const scoreByDriverId = new Map(ranking.items.map(e => [e.driverId, rankingEntryToScore(e)]));
    return drivers.map(d => scoreByDriverId.has(d.id) ? { ...d, score: scoreByDriverId.get(d.id) } : d);
  } catch {
    return drivers;
  }
}

// La carga completa (conductores + sub-unidades + score) va detrás de
// `shareInFlight`: 18 sitios montan este hook a la vez (cada `AlertCard` de "Mi
// Desempeño" es uno) y sin esto cada instancia pedía lo mismo por su cuenta.
// La clave lleva el rol Y el usuario: el score que llega es distinto para cada
// uno (`/me/score` frente a `/rankings`).
const loadDrivers = shareInFlight(
  (role: string | undefined, userId: string | undefined) => `${role ?? ''}:${userId ?? ''}`,
  async (role: string | undefined, userId: string | undefined): Promise<Driver[]> => {
    const [items, subunitNames] = await Promise.all([getAllDrivers(), getSubunitNames()]);
    const mapped = items.map(dto => driverFromDto(dto, subunitNames.get(dto.subunitId ?? '') ?? ''));
    return withScores(mapped, role, userId);
  },
);

export function useDrivers() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const withScore = await loadDrivers(user?.role, user?.id);
        if (cancelled) return;
        setDrivers(withScore);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setDrivers([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando conductores'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.role, user?.id]);

  return { drivers, isLoading, error };
}
